
import React from 'react';
import { Card } from '@/components/ui/card';
import { useCustomerData } from '@/hooks/useCustomerData';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIsMobile } from '@/hooks/use-mobile';
import CustomerTable from '@/components/admin/CustomerTable';
import CustomerCards from '@/components/admin/CustomerCards';
import RefreshDataButton from '@/components/dashboard/RefreshDataButton';
import { Button } from '@/components/ui/button';
import { LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import ExportButton from '@/components/admin/ExportButton';
import UserStats from '@/components/admin/UserStats';
import AuthErrorHandler from '@/components/admin/errors/AuthErrorHandler';
import { CustomerData } from '@/hooks/admin/types';

const CustomerTabContent: React.FC = () => {
  const { 
    customers, 
    isLoading, 
    error,
    searchTerm,
    setSearchTerm,
    handleSort,
    sortColumn,
    sortDirection,
    refreshData,
    isRefreshing,
    updateCustomerPlan,
    createCustomerSubscription,
    cancelCustomerSubscription,
    planFilter,
    setPlanFilter,
    customerStats
  } = useCustomerData();
  
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Check if authentication error
  const isAuthError = error && (
    error.includes('Authentication') || 
    error.includes('sign in') ||
    error.includes('session')
  );

  return (
    <Card className="p-4 md:p-6">
      {!isLoading && !error && (
        <UserStats 
          customers={customers} 
          planFilter={planFilter}
          onFilterChange={setPlanFilter}
          stats={customerStats}
        />
      )}
      
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <Label htmlFor="search" className="md:sr-only">Search Customers</Label>
        <div className="flex flex-col md:flex-row gap-2 md:items-center w-full">
          <Input 
            type="search"
            id="search"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="md:max-w-xs"
          />
          <div className="ml-auto flex gap-2">
            <ExportButton 
              customers={customers}
              isDisabled={isLoading || isRefreshing || !!error}
            />
            <RefreshDataButton 
              onClick={refreshData} 
              isRefreshing={isRefreshing} 
            />
          </div>
        </div>
      </div>
      
      {isLoading && <p className="py-4 text-center">Loading customer data...</p>}
      
      {error && (
        <AuthErrorHandler 
          error={error} 
          isAuthError={isAuthError} 
          onRefresh={refreshData}
        />
      )}
      
      <div className="overflow-x-auto">
        {!error && !isLoading && customers.length === 0 && (
          <p className="py-4 text-center text-muted-foreground">No customer data available.</p>
        )}
        
        {!error && customers.length > 0 && (
          isMobile ? (
            <CustomerCards 
              customers={customers} 
              updateCustomerPlan={updateCustomerPlan}
              createCustomerSubscription={createCustomerSubscription}
              cancelCustomerSubscription={cancelCustomerSubscription}
            />
          ) : (
            <CustomerTable 
              customers={customers} 
              handleSort={handleSort} 
              sortColumn={sortColumn as keyof CustomerData} 
              sortDirection={sortDirection} 
              updateCustomerPlan={updateCustomerPlan}
              createCustomerSubscription={createCustomerSubscription}
              cancelCustomerSubscription={cancelCustomerSubscription}
            />
          )
        )}
      </div>
    </Card>
  );
};

export default CustomerTabContent;
